import { IonButton, IonCard, IonCardContent, IonCardHeader, IonIcon, IonRow } from '@ionic/react';
import { informationCircle } from 'ionicons/icons';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';
import { TimeTable } from 'src/utils/logs/logHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { DqText } from '../dq-text';
import SystemUsageModal from '../job-details/information/information-modals/SystemUsageModal';
import { Range, RangeSet } from './utils';
import { PieChart } from './wrappers/PieChart';
import { PieChartDataEntry } from './wrappers/PieChartController';

export interface PieInfo {
  title: string;
  valve5: JobLogV2Data['valveStatus'][number];
  valve6: JobLogV2Data['valveStatus'][number];
  valve2: JobLogV2Data['valveStatus'][number];
  /** The time table used to correlate events with their calendar time. */
  timeTable: JobLogV2Data['timeTable'] | undefined;
  /** Time range based on job ms elapsed */
  timeRange: { min: number; max: number };
  showTimeDetails: boolean;
  controllerOffRanges: RangeSet;
  /** Time range based on calendar time if FW v >= 2.3.18, otherwise based on job ms elapsed and matches timeRange */
  logFileTimeRangeMin: number;
  logFileTimeRangeMax: number;
}

export const PieSentinelSystem: React.FC<PieInfo> = ({
  title,
  valve5,
  valve6,
  valve2,
  timeTable,
  timeRange,
  showTimeDetails,
  controllerOffRanges,
  logFileTimeRangeMin,
  logFileTimeRangeMax,
}) => {
  const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false);

  const { formatMessage } = useIntl();

  const time = useMemo(() => {
    if (!timeTable?.length) return undefined;
    return TimeTable.from(timeTable);
  }, [timeTable]);

  const positioningEvents = useMemo(() => {
    return [valve5.valveA, valve5.valveB, valve6.valveA, valve6.valveB].flat();
  }, [valve5.valveA, valve5.valveB, valve6.valveA, valve6.valveB]);

  const positioningRange = useMemo(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...positioningEvents.map(Range.fromValve));
    } catch (err) {
      console.warn('Error parsing positioning range for system pie chart.', err);
    }
    return set;
  }, [positioningEvents, logFileTimeRangeMin, logFileTimeRangeMax]);

  const feedingEvents = useMemo(() => {
    return [valve2.valveA, valve2.valveB].flat();
  }, [valve2.valveA, valve2.valveB]);

  const feedingRange = useMemo(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...feedingEvents.map(Range.fromValve));
    } catch (err) {
      console.warn('Error parsing feeding range for system pie chart.', err);
    }
    return set;
  }, [feedingEvents, logFileTimeRangeMin, logFileTimeRangeMax]);

  /** The duration in ms that the job took, counting only moments while the controller was on. */
  const totalRuntime = timeRange.max - timeRange.min;

  /** The duration in ms that the job took in real (calendar) time. */
  const totalCalendarTime = useMemo(() => {
    if (!time) return 0;
    return time.getInstant(logFileTimeRangeMax) - time.getInstant(logFileTimeRangeMin);
  }, [time, logFileTimeRangeMax, logFileTimeRangeMin]);

  const controllerOffTime = Math.max(totalCalendarTime - totalRuntime, 0);

  /** For Controller FW >= 2.3.18: Get all times controller was off during the range so we can exclude it from the idle time */
  const filteredControllerOffRanges = useMemo(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...controllerOffRanges.getRangesBeforePointInTime(logFileTimeRangeMax));
    } catch (err) {
      console.warn('Error parsing off range for system pie chart.', err);
    }
    return set;
  }, [controllerOffRanges, logFileTimeRangeMin, logFileTimeRangeMax]);

  const data = useMemo(() => {
    const positioningTime = positioningRange.sumIncluded();
    const feedingTime = feedingRange.sumIncluded();
    const idleTime = positioningRange.union(feedingRange).union(filteredControllerOffRanges).sumExcluded();

    const dataArray: PieChartDataEntry[] = [];

    dataArray.push({
      key: 'positioning',
      label: formatMessage(JobMessages.positioningLegend),
      value: showTimeDetails ? positioningTime : (positioningTime / totalCalendarTime) * 100, // obfuscate the actual time via percentage calculation, if showTimeDetails = false
      color: 'data-blue',
    });
    dataArray.push({
      key: 'lance',
      label: formatMessage(JobMessages.lanceFeedingLegend),
      value: showTimeDetails ? feedingTime : (feedingTime / totalCalendarTime) * 100,
      color: 'data-green',
    });
    dataArray.push({
      key: 'ctrlOff',
      label: formatMessage(JobMessages.controllerOffLegend),
      value: showTimeDetails ? controllerOffTime : (controllerOffTime / totalCalendarTime) * 100,
      color: 'data-orange',
    });
    dataArray.push({
      key: 'idle',
      label: formatMessage(JobMessages.idleLegend),
      value: showTimeDetails ? idleTime : (idleTime / totalCalendarTime) * 100,
      color: 'data-yellow',
    });

    return dataArray;
  }, [
    positioningRange,
    feedingRange,
    formatMessage,
    showTimeDetails,
    totalCalendarTime,
    controllerOffTime,
    filteredControllerOffRanges,
  ]);

  return (
    <IonCard>
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <DqText variant="h3">{title}</DqText>
          <IonButton fill="clear" color="primary" onClick={() => setHelpDialogOpen(true)}>
            <IonIcon slot="icon-only" icon={informationCircle} />
          </IonButton>
        </IonRow>
      </IonCardHeader>
      <IonCardContent>
        {data ? (
          <PieChart data={data} showTimeDetails={showTimeDetails} />
        ) : (
          <DqText>{formatMessage(JobMessages.noSystemStateData)}</DqText>
        )}
      </IonCardContent>
      <SystemUsageModal isOpen={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </IonCard>
  );
};
